import { EntityType } from "@mapmycustomers/shared";
import FieldFeature from "@mapmycustomers/shared/enum/fieldModel/FieldFeature";
import { ActivityFilterEntityType } from "@mapmycustomers/shared/types/entity";
import IFieldModel from "@mapmycustomers/shared/types/fieldModel/IFieldModel";
import {
  CombinedCondition,
  FilterModel,
} from "@mapmycustomers/shared/types/viewModel/internalModel/FilterModel";

import CommonFieldName from "@app/util/fieldModel/CommonFieldName";
import getFieldModelByEntityType from "@app/util/fieldModel/getByEntityType";
import {
  addEntityTypePrefixRecursively,
  removeEntityTypePrefixRecursively,
} from "@app/util/filters/prefixUtils";
import { createEmptyFilterModel } from "@app/util/filters/utils";
import { isCombinedCondition, isSimpleCondition } from "@app/util/viewModel/assert";
import { getFieldEntityType } from "@app/util/viewModel/getFieldEntityType";

const getCombinedConditionEntityTypes = (
  condition: CombinedCondition,
  haltOnMix?: boolean
): ActivityFilterEntityType[] => {
  let result = new Set<ActivityFilterEntityType>();
  for (const subCondition of condition.conditions) {
    if (isSimpleCondition(subCondition)) {
      result.add(getFieldEntityType(subCondition.fieldFilterName) ?? EntityType.ACTIVITY);
    } else {
      result = new Set([...getCombinedConditionEntityTypes(subCondition), ...result]);
    }
    // we don't care about a full set of entity types and exit as soon
    // as it is bigger than 1, which means we have a mixed set of field
    // here from at least 2 different entities (A/C/P/D)
    if (haltOnMix && result.size > 1) {
      break;
    }
  }
  return Array.from(result);
};

// Return values mean:
//   ActivityEntityTypes if the type found and confirmed
//   undefined if the combined condition is empty, hence we need to skip it
//   false if the combined condition has mixed field types, and hence is invalid
const getCombinedConditionTag = (
  condition: CombinedCondition
): ActivityFilterEntityType | false | undefined => {
  // if tag is present, we believe it
  if (condition.tag && condition.tag !== "universal") {
    return condition.tag;
  }

  const entityTypes = getCombinedConditionEntityTypes(condition, true);
  if (entityTypes.length === 0) {
    return undefined;
  } else if (entityTypes.length > 1) {
    return false;
  }
  return entityTypes[0];
};

const removeUnsupportedFilters = (
  fieldModel: IFieldModel,
  condition: CombinedCondition
): CombinedCondition => {
  return {
    ...condition,
    conditions: condition.conditions.reduce(
      (result, condition) => {
        if (isCombinedCondition(condition)) {
          result.push(removeUnsupportedFilters(fieldModel, condition));
          return result;
        }

        const field = fieldModel.getByFilterName(condition.fieldFilterName);
        if (
          field &&
          (field.hasAnyFeature(
            FieldFeature.CUSTOM_FIELD,
            FieldFeature.RELATIONSHIPS_CATEGORY,
            FieldFeature.TEAM_FIELD
          ) ||
            field.name === CommonFieldName.VIEW_AS ||
            field.name === CommonFieldName.NOTES)
        ) {
          return result;
        }

        result.push(condition);
        return result;
      },
      [] as CombinedCondition["conditions"]
    ),
  };
};

// Takes a given FilterModel and converts it to a field model where up
// to 4 combined conditions are present on the top level, each
// tagged with A/C/P/D type.
// It groups related conditions into one of these top-level groups.
// If it is not able to do that, for example, when there's a
// combined condition with a mix of A/C/P/D fields, it will
// return isIncompatible=true and an empty result.
export const getTaggedFilterModelForActivities = (
  filterModel: FilterModel
): [taggedFilterModel: FilterModel, isIncompatible: boolean] => {
  // A map which points at indices we found conditions of a certain type at.
  // If any rules for activity or relationships are present on the top level,
  // we need to wrap them in a combined condition. So only 4 combined conditions
  // max are present at the top level (one per A/C/P/D types).
  // Also, if multiple combined conditions of the same type are present at the top,
  // we will need to wrap them in a combined condition too.
  const conditionIndexMap: Partial<Record<ActivityFilterEntityType, number[]>> = {};

  for (let i = 0; i < filterModel.conditions.length; i++) {
    const condition = filterModel.conditions[i];
    let tag: ActivityFilterEntityType | false | undefined = undefined;
    if (isSimpleCondition(condition)) {
      tag = getFieldEntityType(condition.fieldFilterName) ?? EntityType.ACTIVITY;
    } else {
      tag = getCombinedConditionTag(condition);
    }
    if (tag === false) {
      // nothing else we can do here, let's exit with an error
      return [createEmptyFilterModel(), true];
    }
    if (!tag) {
      continue;
    }
    if (!conditionIndexMap[tag]) {
      conditionIndexMap[tag] = [];
    }
    conditionIndexMap[tag]!.push(i);
  }

  // now create a filter model that will have the same rules, but grouped
  // into 4 top-level combined conditions with tags (A/C/P/D)
  const result = createEmptyFilterModel();
  (Object.keys(conditionIndexMap) as ActivityFilterEntityType[]).forEach((tag) => {
    const conditionsIndices = conditionIndexMap[tag]!;
    if (conditionsIndices.length > 1) {
      result.conditions.push({
        conditions: conditionsIndices.map((index) => filterModel.conditions[index]),
        operator: filterModel.operator,
        tag,
      });
    } else {
      const condition = filterModel.conditions[conditionsIndices[0]];
      if (isSimpleCondition(condition)) {
        result.conditions.push({ conditions: [condition], operator: filterModel.operator, tag });
      } else {
        result.conditions.push({ ...condition, tag });
      }
    }
  });

  result.conditions = result.conditions.map((condition) => {
    if (
      !isCombinedCondition(condition) ||
      !condition.tag ||
      condition.tag === "universal" ||
      condition.tag === EntityType.ACTIVITY
    ) {
      return condition;
    }

    const entityType = condition.tag;
    return addEntityTypePrefixRecursively(
      removeUnsupportedFilters(
        getFieldModelByEntityType(entityType),
        removeEntityTypePrefixRecursively(condition)
      ),
      entityType
    );
  });

  return [result, false];
};
